<template>
    <div class="quick-reply-message-editor" :key="renderId">
        <TiptapEditor
            :placeholder="$t('chatbotv2.enterYourMessage')"
            :show-attributes="showAttributes"
            :show-emoji="showEmoji"
            :max-character-count="640"
            v-model="clone.message"
            :whatsapp-mode="whatsappMode"
            :allowed-attributes="allowedAttributes"
            :show-emoji-picker="showEmojiPicker"
        />
        
        <div class="quick-replies">
            <div class="quick-reply" v-for="(button, i) in clone.buttons" :key="button.id">
                <a-popover
                    :visible="editingButtonPopupVisibleMap[button.id]"
                    placement="left"
                    @visibleChange="(e) => visibleChange(button.id, e)"
                    trigger="click"
                    :key="button.id"
                    :overlayClassName="'no-padding'"
                >
                    <div slot="content">
                        <ActionButtonUpdater
                            v-model="clone.buttons[i]"
                            :on-completed="
                                () => {
                                    editingButtonPopupVisibleMap[button.id] = false;
                                }
                            "
                            :popup-visible="editingButtonPopupVisibleMap[button.id]"
                            :allowed-message-types="[
                                'SEND_MESSAGE',
                                'GO_TO_BLOCK',
                                'OPEN_URL',
                                'PHONE_CALL',
                                'OPEN_MOMENT',
                                'CLOSE_CHAT',
                                'TRANSFER_CHAT',
                                'CREATE_TICKET',
                            ]"
                            :flattedDiagram="flattedDiagram"
                            :whatsapp-mode="whatsappMode"
                            :allowed-attributes="allowedAttributes"
                        />
                    </div>
                    <div class="quick-reply-text">{{ button.title }}</div>
                    <div class="button-controls" v-if="clone.buttons.length > 1">
                        <md-button class="md-icon-button md-mini" @click="removeButton(button.id)">
                            <md-icon>delete_outline</md-icon>
                        </md-button>
                    </div>
                </a-popover>
            </div>
            <div v-if="!previewMode" class="quick-reply-add" @click="addButton">
                <span class="material-icons">add</span>
                <span>{{ $t("chatbotv2.addButton") }}</span>
            </div>
            
            <!-- WhatsApp Settings Button -->
            <div v-if="clone.buttons.length > 3 && clone.buttons.length <= 10 && !previewMode" class="quick-reply-add whatsapp-settings" @click="showWhatsappSettings = true">
                <span class="material-icons">settings</span>
                <span>WhatsApp Ayarları</span>
            </div>
        </div>
        
        <!-- WhatsApp Settings Modal -->
        <WhatsappListSettingsModal
            v-if="showWhatsappSettings"
            :value="clone.whatsappListSettings"
            @save="handleWhatsappSettingsSave"
            @cancel="showWhatsappSettings = false"
        />
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import TiptapEditor from "../../common/TiptapEditor";
import { generateRandomId } from "../../../../../../../utils/session";
import ActionButtonUpdater from "../../common/ActionButtonUpdater";
import WhatsappListSettingsModal from "./WhatsappListSettingsModal.vue";
import _ from "lodash";

const log = log2("QuickReplyMessageEditor");
export default {
    name: "QuickReplyMessageEditor",
    components: { TiptapEditor, ActionButtonUpdater, WhatsappListSettingsModal },
    props: {
        value: {
            type: Object,
            required: true,
            default: {
                type: "BUTTON",
                delay: 2,
                message: "",
                buttons: [
                    {
                        id: "",
                        title: "Button",
                        type: "SEND_MESSAGE",
                        payload: {},
                        actions: [], //{type: "SET_ATTRIBUTE", payload: {attribute:"custom",value:1} },
                    },
                ],
            },
        },
        flattedDiagram: {
            type: Array,
            default: () => [],
        },
        allowedAttributes: {
            type: Array,
            default: () => [],
        },
        whatsappMode: {
            type: Boolean,
            default: false,
        },
        previewMode: {
            type: Boolean,
            default: false,
        },
        showEmoji: {
            type: Boolean,
            default: true,
        },
        showAttributes: {
            type: Boolean,
            default: true,
        },
        showEmojiPicker: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        const clonedValue = _.cloneDeep(this.value);
        // Ensure whatsappListSettings exists for backward compatibility
        if (!clonedValue.whatsappListSettings) {
            clonedValue.whatsappListSettings = {
                globalButtonTitle: "",
                sectionTitle: "",
                sectionSubTitle: "",
                footer: "",
            };
        }
        return {
            editingButtonPopupVisibleMap: {},
            renderId: 0,
            clone: clonedValue,
            showWhatsappSettings: false,
        };
    },
    methods: {
        openEditButtonPopup(buttonId) {
            Object.keys(this.editingButtonPopupVisibleMap).forEach((key) => {
                this.editingButtonPopupVisibleMap[key] = false;
            });
            this.editingButtonPopupVisibleMap[buttonId] = true;
        },
        visibleChange(buttonId, visible) {
            log("visibleChange", buttonId, visible);
            Object.keys(this.editingButtonPopupVisibleMap).forEach((key) => {
                this.editingButtonPopupVisibleMap[key] = false;
            });
            this.editingButtonPopupVisibleMap[buttonId] = visible;
            this.renderId++;
        },
        addButton() {
            const id = generateRandomId();
            this.openEditButtonPopup(id);
            this.clone = {
                ...this.clone,
                buttons: [
                    ...this.clone.buttons,
                    {
                        id,
                        title: `Button ${this.clone.buttons.length + 1}`,
                        type: "SEND_MESSAGE",
                        payload: {},
                        actions: [], //{type: "SET_ATTRIBUTE", payload: {attribute:"custom",value:1} },
                    },
                ],
            };
        },
        removeButton(id) {
            this.clone = {
                ...this.clone,
                buttons: this.clone.buttons.filter((button) => button.id !== id),
            };
        },
        handleWhatsappSettingsSave(settings) {
            this.clone.whatsappListSettings = settings;
            this.showWhatsappSettings = false;
        },
        emitValue() {
            if (!_.isEqual(this.clone, this.value)) {
                log("emitValue", this.clone);
                this.$emit("input", this.clone);
            }
        },
    },
    mounted() {
        log("mounted");
    },
    watch: {
        value: {
            handler(val) {
                log("watch value-->", val);
                const clonedVal = _.cloneDeep(val);
                // Ensure whatsappListSettings exists for backward compatibility
                if (!clonedVal.whatsappListSettings) {
                    clonedVal.whatsappListSettings = {
                        globalButtonTitle: "",
                        sectionTitle: "",
                        sectionSubTitle: "",
                        footer: "",
                    };
                }
                this.clone = clonedVal;
            },
            deep: true,
        },
        clone: {
            handler(val) {
                log("watch clone-->", val);
                this.emitValue();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.ProseMirror {
    color: #22272d;
    font-size: 16px;
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale;
    font-family: Source Sans Pro, sans-serif;
    font-weight: 400;
}

.quick-reply-message-editor {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    width: 100%;

    .quick-replies {
        display: flex;
        flex-wrap: wrap;
        margin: 6px -5px 0;
        width: 100%;

        .quick-reply {
            background: #fff;
            border: 1px solid #006cff;
            border-radius: 16px;
            color: #006cff;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            height: 32px;
            line-height: 30px;
            margin: 10px 5px 0;
            min-width: 100px;
            padding: 0 16px;
            position: relative;
            text-align: center;
            transition: all 0.3s;

            &:hover {
                .button-controls {
                    opacity: 1;
                }
            }

            .button-controls {
                align-items: center;
                display: flex;
                opacity: 0;
                position: absolute;
                right: -13px;
                top: -13px;
                transition: opacity 0.3s;

                .md-button {
                    background-color: #fff;
                    align-items: center;
                    border-radius: 100%;
                    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                    cursor: pointer;
                    display: flex;
                    justify-content: center;
                    margin-bottom: 7px;
                    transition: background-color 0.3s;
                    height: 30px;
                    width: 30px;
                    min-height: 30px !important;
                    min-width: 30px !important;

                    .md-icon {
                        color: #006cff;
                    }
                }
            }
        }

        .quick-reply-add {
            border: 1px dashed #979797;
            border-radius: 16px;
            color: #22272d;
            cursor: pointer;
            font-size: 13px;
            font-weight: 600;
            height: 32px;
            line-height: 30px;
            margin: 10px 5px 0;
            order: 1;
            padding: 0 12px;
            transition: all 0.3s;
            display: flex;
            align-items: center;
            gap: 4px;

            .material-icons {
                font-size: 16px;
            }

            &:hover {
                border-color: #006cff;
                color: #006cff;
            }
            
            &.whatsapp-settings {
                border: 1px solid #25D366;
                background: #25D366;
                color: white;
                
                &:hover {
                    background: #128C7E;
                    border-color: #128C7E;
                    transform: translateY(-2px);
                    box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
                }
            }
        }
    }
}
</style>
